'use client';

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Heart } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ProductCardProps } from './types';
import {
  decodeHtmlEntities,
  formatPrice,
  normalizeLabels,
  createNameExtractor,
} from './utils';

/**
 * Компонент карточки товара с поддержкой многоязычности и валют
 */
export function ProductCard({
  item,
  langCode,
  currency,
  nameExtractor,
  favoriteManager,
  favoriteDataExtractor,
  showPrices = true,
  showDiscount = true,
  className = '',
  itemPath = '/shop/item',
  fallbackImageUrl = '/placeholder.png',
  aspectRatio = '3/4',
  imageFit = 'contain',
}: ProductCardProps) {
  // Нормализуем labels
  const labels = useMemo(() => normalizeLabels(item), [item]);

  // Получаем функцию для извлечения названия
  const getName = useMemo(() => {
    return nameExtractor || createNameExtractor();
  }, [nameExtractor]);

  // Извлекаем название товара с учетом языка
  const productName = useMemo(() => {
    return getName(item, langCode) || 'Товар';
  }, [getName, item, langCode]);

  // Извлекаем данные для избранного
  const favoriteData = useMemo(() => {
    if (!favoriteDataExtractor) {
      return null;
    }
    return favoriteDataExtractor(item);
  }, [favoriteDataExtractor, item]);

  // Определяем состояние избранного
  const favoriteId = favoriteData?.id;
  const favoriteActive =
    favoriteId && favoriteManager?.isHydrated
      ? favoriteManager.isFavorite(favoriteId)
      : false;

  // Обработчик переключения избранного
  const handleFavoriteToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (favoriteData && favoriteManager) {
        favoriteManager.toggleFavorite(favoriteData);
      }
    },
    [favoriteData, favoriteManager]
  );

  // Получаем цену
  const price = useMemo(() => {
    const priceValue =
      item?.price ??
      item?.languages?.[langCode]?.price;
    return typeof priceValue === 'number'
      ? priceValue
      : typeof priceValue === 'string'
      ? Number(priceValue)
      : 0;
  }, [item, langCode]);

  // Получаем старую цену
  const priceOld = useMemo(() => {
    const priceOldValue =
      item?.priceOld ??
      item?.languages?.[langCode]?.priceOld;
    return typeof priceOldValue === 'number'
      ? priceOldValue
      : typeof priceOldValue === 'string'
      ? Number(priceOldValue)
      : 0;
  }, [item, langCode]);

  // Получаем массив всех изображений (максимум 4)
  const imageGallery = useMemo(() => {
    const images: string[] = [];
    
    // Собираем все доступные изображения из разных источников
    
    // 1. Из media.gallery
    const gallery = item?.media?.gallery;
    if (Array.isArray(gallery) && gallery.length > 0) {
      images.push(...gallery.filter(Boolean));
    }
    
    // 2. Из image_gallery (альтернативный источник)
    const imageGalleryAlt = item?.image_gallery;
    if (Array.isArray(imageGalleryAlt) && imageGalleryAlt.length > 0) {
      images.push(...imageGalleryAlt.filter(Boolean));
    }
    
    // 3. Из media.cover
    const cover = item?.media?.cover;
    if (cover && typeof cover === 'string' && cover.trim() && !images.includes(cover)) {
      images.unshift(cover); // Добавляем cover в начало
    }
    
    // 4. Из picture
    const picture = item?.picture;
    if (picture && typeof picture === 'string' && picture.trim() && !images.includes(picture)) {
      images.unshift(picture); // Добавляем picture в начало
    }
    
    // 5. Из image (еще один возможный источник)
    const image = item?.image;
    if (image && typeof image === 'string' && image.trim() && !images.includes(image)) {
      images.unshift(image);
    }
    
    // Удаляем дубликаты и фильтруем пустые значения
    const uniqueImages = Array.from(new Set(images.filter(Boolean)));
    
    // Ограничиваем до 4 изображений для оптимизации
    const limitedImages = uniqueImages.slice(0, 4);
    
    // Если нет изображений, используем fallback
    return limitedImages.length > 0 ? limitedImages : [fallbackImageUrl];
  }, [item, fallbackImageUrl]);

  // Состояние для текущего индекса изображения
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  
  // Touch-события для мобильных
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const isSwipingRef = useRef(false);
  const shouldPreventClickRef = useRef(false);

  // Сбрасываем индекс при изменении товара
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [item?.code, item?.id]);

  // Переключение на следующее изображение
  const goToNextImage = useCallback(() => {
    if (imageGallery.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % imageGallery.length);
  }, [imageGallery.length]);

  // Переключение на предыдущее изображение
  const goToPreviousImage = useCallback(() => {
    if (imageGallery.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + imageGallery.length) % imageGallery.length);
  }, [imageGallery.length]);

  // Переключение на конкретный индекс
  const goToImage = useCallback((index: number) => {
    if (index >= 0 && index < imageGallery.length) {
      setCurrentImageIndex(index);
    }
  }, [imageGallery.length]);

  // Hover-логика для десктопа
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (imageGallery.length <= 1) return;
    if (!imageContainerRef.current) return;

    const rect = imageContainerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const width = rect.width;
    
    // Вычисляем индекс изображения на основе позиции мышки
    // Разделяем область на части по количеству изображений
    const imageIndex = Math.floor((mouseX / width) * imageGallery.length);
    const clampedIndex = Math.min(Math.max(0, imageIndex), imageGallery.length - 1);
    
    // Обновляем индекс, если он изменился
    if (clampedIndex !== currentImageIndex) {
      setCurrentImageIndex(clampedIndex);
    }
  }, [imageGallery.length, currentImageIndex]);

  const handleMouseLeave = useCallback(() => {
    // Возвращаемся к первому изображению при уходе мышки
    setCurrentImageIndex(0);
  }, []);

  // Touch-логика для мобильных
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (imageGallery.length <= 1) return;
    const touch = e.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    isSwipingRef.current = false;
    shouldPreventClickRef.current = false;
  }, [imageGallery.length]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (imageGallery.length <= 1 || touchStartXRef.current === null) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - (touchStartYRef.current || 0);
    
    // Определяем, что это свайп, а не скролл
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isSwipingRef.current = true;
      e.preventDefault(); // Предотвращаем скролл при свайпе
    }
  }, [imageGallery.length]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (imageGallery.length <= 1 || touchStartXRef.current === null) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      isSwipingRef.current = false;
      shouldPreventClickRef.current = false;
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const swipeThreshold = 50; // Минимальное расстояние для свайпа

    // Если был свайп - переключаем изображение и предотвращаем переход
    if (isSwipingRef.current && Math.abs(deltaX) > swipeThreshold) {
      shouldPreventClickRef.current = true; // Устанавливаем флаг для предотвращения клика
      if (deltaX > 0) {
        // Свайп вправо - предыдущее изображение
        goToPreviousImage();
      } else {
        // Свайп влево - следующее изображение
        goToNextImage();
      }
      // Сбрасываем флаг через небольшую задержку, чтобы onClick успел его проверить
      setTimeout(() => {
        shouldPreventClickRef.current = false;
      }, 100);
    } else {
      // Если это был обычный клик (не свайп) - разрешаем переход
      shouldPreventClickRef.current = false;
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
    isSwipingRef.current = false;
  }, [imageGallery.length, goToNextImage, goToPreviousImage]);


  // Получаем код товара для ссылки
  const itemCode = item?.code ?? item?.id ?? '';

  // Вычисляем процент скидки
  const discountPercent = useMemo(() => {
    if (!showDiscount || priceOld <= 0 || priceOld <= price) {
      return 0;
    }
    return Math.round(((priceOld - price) / priceOld) * 100);
  }, [price, priceOld, showDiscount]);

  // Форматируем цены
  const formattedPrice = formatPrice(price, currency, langCode);
  const formattedPriceOld = formatPrice(priceOld, currency, langCode);

  const showOldPrice = showDiscount && priceOld > 0 && priceOld > price;

  return (
    <div
      className={cn(
        'relative mb-2 w-full rounded-md bg-white flex flex-col hover:text-blue-500',
        className
      )}
    >
      {/* Кнопка избранного */}
      {favoriteManager && favoriteData && (
        <button
          type="button"
          onClick={handleFavoriteToggle}
          aria-pressed={favoriteActive}
          aria-label={favoriteActive ? `Удалить "${productName}" из избранного` : `Добавить "${productName}" в избранное`}
          className={cn(
            'absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring focus-visible:ring-offset-2 focus-visible:ring-red-400',
            favoriteActive
              ? 'border-red-200 bg-red-50 text-red-500'
              : 'border-default-200 bg-white text-default-500 hover:text-red-500'
          )}
          style={{ pointerEvents: 'auto' }}
        >
          <Heart className="h-4 w-4" fill={favoriteActive ? 'currentColor' : 'none'} aria-hidden="true" />
        </button>
      )}

      {/* Ссылка на товар */}
      <a 
        href={`${itemPath}/${itemCode}`} 
        className="flex h-full flex-col"
        onClick={(e) => {
          // Предотвращаем переход только если был свайп
          if (shouldPreventClickRef.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {/* Изображение товара */}
        <div 
          ref={imageContainerRef}
          className="relative border rounded overflow-hidden bg-white flex items-center justify-center group"
          style={{
            minHeight: 0,
            position: 'relative',
            aspectRatio: aspectRatio
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Labels/Badges */}
          {labels.length > 0 && (
            <div className="absolute left-2 top-2 flex flex-col gap-1 z-10">
              {labels.map((label) => {
                const meta = label.meta || {};
                const metaKey =
                  meta.xml_id ||
                  meta.enum_id ||
                  meta.raw ||
                  label.value ||
                  label.name ||
                  label.code ||
                  'label';
                const badgeText = decodeHtmlEntities(label.value || label.name || '');
                const titleSource =
                  meta.name && meta.name !== badgeText ? meta.name : label.name;
                const title =
                  titleSource && titleSource !== badgeText
                    ? decodeHtmlEntities(titleSource)
                    : undefined;
                const baseClass =
                  'inline-flex w-fit items-center rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide border shadow-sm';
                const themedClass = meta.bg_color
                  ? ''
                  : label.source === 'offer'
                  ? 'bg-indigo-600/10 text-indigo-600 border-indigo-200'
                  : 'bg-gray-900 text-white border-gray-900';

                const style: React.CSSProperties = {};
                if (meta.bg_color) {
                  style.backgroundColor = meta.bg_color;
                  style.borderColor = meta.bg_color;
                }
                if (meta.text_color) {
                  style.color = meta.text_color;
                }

                return (
                  <span
                    key={`${label.code || 'label'}:${metaKey}:${label.source || 'product'}`}
                    title={title}
                    data-source={label.source || 'product'}
                    className={cn(baseClass, themedClass)}
                    style={style}
                  >
                    {badgeText}
                  </span>
                );
              })}
            </div>
          )}

          {/* Изображения карусели */}
          <div className="relative w-full h-full">
            {imageGallery.map((imageUrl, index) => (
              <div
                key={`${imageUrl}-${index}`}
                className={cn(
                  "absolute inset-0 transition-opacity duration-300",
                  index === currentImageIndex ? "opacity-100 z-0" : "opacity-0 z-0"
                )}
              >
                <Image
                  src={imageUrl}
                  alt={`${productName} - ${index + 1}`}
                  fill
                  sizes="(min-width: 1280px) 240px, (min-width: 768px) 220px, 160px"
                  className={cn("object-center", imageFit === 'cover' ? 'object-cover' : 'object-contain')}
                  quality={75}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  style={{
                    backgroundColor: 'transparent'
                  }}
                  aria-hidden={index !== currentImageIndex}
                />
              </div>
            ))}
          </div>

          {/* Индикаторы (dots) */}
          {imageGallery.length > 1 && (
            <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5 z-10">
              {imageGallery.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToImage(index);
                  }}
                  className={cn(
                    "w-1.5 h-1.5 rounded-full transition-all duration-200",
                    index === currentImageIndex
                      ? "bg-blue-500 w-4"
                      : "bg-gray-300 hover:bg-gray-400"
                  )}
                  aria-label={`Перейти к изображению ${index + 1}`}
                  aria-current={index === currentImageIndex ? 'true' : 'false'}
                />
              ))}
            </div>
          )}
        </div>

        {/* Информация о товаре */}
        <div className="p-2">
          {/* Процент скидки */}
          {showPrices && showDiscount && discountPercent > 0 && (
            <div className="text-xs text-red-500 font-medium mb-1">-{discountPercent}%</div>
          )}

          {/* Цены */}
          {showPrices && (
            <div className="flex gap-2 items-center">
              {price > 0 && formattedPrice && (
                <div className="font-bold text-default-900">{formattedPrice}</div>
              )}
              {showOldPrice && formattedPriceOld && (
                <div className="text-sm text-gray-400 line-through">{formattedPriceOld}</div>
              )}
            </div>
          )}

          {/* Название товара */}
          <div
            className="text-sm line-clamp-3 leading-snug min-h-[3.5rem]"
            title={decodeHtmlEntities(productName)}
          >
            {decodeHtmlEntities(productName)}
          </div>
        </div>
      </a>
    </div>
  );
}

